<script setup>
import { computed } from 'vue'
import {
  BRADLEY_ATTACHMENT_STATUS,
  formatBradleyFileSize,
  getBradleyAttachmentSizeBytes,
  getBradleyAttachmentStatus,
  isBradleyImageAttachment,
} from '../utils/bradley-attachment.util.js'

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  showStatus: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['remove'])

const stagedItems = computed(() => {
  return (Array.isArray(props.items) ? props.items : []).map((item, index) => {
    const status = getBradleyAttachmentStatus(item)
    const showStatus = props.showStatus
    return {
      item,
      index,
      isImage: isBradleyImageAttachment(item),
      previewUrl: item?.preview_url || item?.previewUrl || '',
      fileName: item?.file_name || item?.name || '',
      sizeLabel: formatBradleyFileSize(getBradleyAttachmentSizeBytes(item)),
      status,
      isUploading: showStatus && status === BRADLEY_ATTACHMENT_STATUS.UPLOADING,
      isSuccess: showStatus && status === BRADLEY_ATTACHMENT_STATUS.SUCCESS,
      isError: showStatus && status === BRADLEY_ATTACHMENT_STATUS.ERROR,
    }
  })
})

function handleRemove(index) {
  emit('remove', index)
}
</script>

<template>
  <div
    v-if="stagedItems.length > 0"
    class="bradley-staged-attachments"
  >
    <div class="bradley-staged-attachments__row">
      <template
        v-for="entry in stagedItems"
        :key="entry.item.client_id || entry.item.file_hash || entry.item.id || entry.item.file_name || entry.index"
      >
        <div
          v-if="entry.isImage"
          class="bradley-staged-attachments__image-card"
          :class="{
            'bradley-staged-attachments__image-card--uploading': entry.isUploading,
            'bradley-staged-attachments__image-card--error': entry.isError,
          }"
        >
          <img
            v-if="entry.previewUrl"
            :src="entry.previewUrl"
            :alt="entry.fileName"
            class="bradley-staged-attachments__image"
            :class="{ 'bradley-staged-attachments__image--dimmed': entry.isUploading || entry.isError }"
          >
          <div
            v-else
            class="bradley-staged-attachments__image bradley-staged-attachments__image--placeholder"
            :class="{ 'bradley-staged-attachments__image--error-placeholder': entry.isError }"
          >
            <i :class="entry.isError ? 'pi pi-image' : 'pi pi-image'" />
          </div>

          <span
            v-if="entry.isUploading"
            class="bradley-staged-attachments__spinner"
            aria-hidden="true"
          >
            <i class="pi pi-spinner pi-spin" />
          </span>

          <span
            v-else-if="entry.isSuccess"
            class="bradley-staged-attachments__success"
            aria-hidden="true"
          >
            <i class="pi pi-check" />
          </span>

          <span
            v-else-if="entry.isError"
            class="bradley-staged-attachments__error-badge"
            aria-hidden="true"
          >
            <i class="pi pi-exclamation-circle" />
          </span>

          <button
            type="button"
            class="bradley-staged-attachments__remove"
            :aria-label="$t('newBradley.input.removeAttachment')"
            @click="handleRemove(entry.index)"
          >
            <i class="pi pi-times" />
          </button>
        </div>

        <div
          v-else
          class="bradley-staged-attachments__document-card"
          :class="{
            'bradley-staged-attachments__document-card--error': entry.isError,
            'bradley-staged-attachments__document-card--uploading': entry.isUploading,
          }"
        >
          <span
            class="bradley-staged-attachments__document-icon"
            :class="{
              'bradley-staged-attachments__document-icon--error': entry.isError,
              'bradley-staged-attachments__document-icon--uploading': entry.isUploading,
            }"
          >
            <i
              v-if="entry.isError"
              class="pi pi-exclamation-circle"
            />
            <i
              v-else
              class="pi pi-file"
            />
            <span
              v-if="entry.isUploading"
              class="bradley-staged-attachments__document-spinner"
              aria-hidden="true"
            >
              <i class="pi pi-spinner pi-spin" />
            </span>
          </span>
          <span class="bradley-staged-attachments__document-meta">
            <span class="bradley-staged-attachments__document-name">
              {{ entry.fileName }}
            </span>
            <span
              v-if="entry.sizeLabel"
              class="bradley-staged-attachments__document-size"
            >
              {{ entry.sizeLabel }}
            </span>
          </span>
          <span
            v-if="entry.isSuccess"
            class="bradley-staged-attachments__document-success"
            aria-hidden="true"
          >
            <i class="pi pi-check" />
          </span>
          <button
            type="button"
            class="bradley-staged-attachments__remove bradley-staged-attachments__remove--document"
            :aria-label="$t('newBradley.input.removeAttachment')"
            @click="handleRemove(entry.index)"
          >
            <i class="pi pi-times" />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.bradley-staged-attachments {
  margin-bottom: 0.75rem;
}

.bradley-staged-attachments__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.bradley-staged-attachments__image-card {
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
}

.bradley-staged-attachments__image {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #e6ebff;
  background: #f8fafc;
}

.bradley-staged-attachments__image--dimmed {
  filter: brightness(0.72);
}

.bradley-staged-attachments__image--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1.25rem;
}

.bradley-staged-attachments__image--error-placeholder {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

.bradley-staged-attachments__spinner,
.bradley-staged-attachments__success,
.bradley-staged-attachments__error-badge {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
}

.bradley-staged-attachments__spinner {
  inset: 0;
  margin: auto;
  width: 1.5rem;
  height: 1.5rem;
  color: #ffffff;
  font-size: 1.125rem;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
}

.bradley-staged-attachments__success {
  right: 4px;
  bottom: 4px;
  width: 1.125rem;
  height: 1.125rem;
  background: #15803d;
  color: #ffffff;
  font-size: 0.625rem;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
}

.bradley-staged-attachments__error-badge {
  right: 4px;
  bottom: 4px;
  width: 1.125rem;
  height: 1.125rem;
  background: #dc2626;
  color: #ffffff;
  font-size: 0.75rem;
}

.bradley-staged-attachments__document-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 49px;
  width: 222px;
  border-radius: 14px;
  border: 1px solid #e6ebff;
  background: #ffffff;
  padding: 0.5rem 1.75rem 0.5rem 0.75rem;
  box-shadow: 0 0 0 1px rgba(230, 235, 255, 0.4);
}

.bradley-staged-attachments__document-card--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.bradley-staged-attachments__document-icon {
  position: relative;
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: #eef2ff;
  color: #374151;
}

.bradley-staged-attachments__document-icon--error {
  background: #fee2e2;
  color: #b91c1c;
}

.bradley-staged-attachments__document-icon--uploading {
  color: #94a3b8;
  position: relative;
}

.bradley-staged-attachments__document-spinner {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: rgba(238, 242, 255, 0.75);
  color: #4f46e5;
  font-size: 0.875rem;
}

.bradley-staged-attachments__document-icon i {
  font-size: 0.875rem;
  line-height: 1;
}

.bradley-staged-attachments__document-meta {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.125rem;
}

.bradley-staged-attachments__document-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
}

.bradley-staged-attachments__document-card--error .bradley-staged-attachments__document-name {
  color: #991b1b;
}

.bradley-staged-attachments__document-size {
  font-size: 0.75rem;
  line-height: 1.2;
  color: #64748b;
}

.bradley-staged-attachments__document-card--error .bradley-staged-attachments__document-size {
  color: #b91c1c;
}

.bradley-staged-attachments__document-success {
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #15803d;
  color: #ffffff;
  font-size: 0.5rem;
}

.bradley-staged-attachments__remove {
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: #0000cc;
  color: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.bradley-staged-attachments__remove:hover {
  background: #0000a8;
}

.bradley-staged-attachments__remove i {
  font-size: 8px;
  line-height: 1;
}

.bradley-staged-attachments__remove--document {
  top: 6px;
  right: 6px;
}
</style>
